import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal, LineReveal, Kicker } from "../components/Reveal";
import { IMAGES, COMPANY } from "../data/site";

const PILLARS = [
    { title: "Company Philosophy", desc: "Engineering first. Every furnace and system is approached as an engineering problem — understood, designed and verified before it is built." },
    { title: "Engineering Approach", desc: "Thermal, electrical and automation engineering are developed together, so the furnace, its power system and its controls work as one." },
    { title: "Manufacturing Approach", desc: "Practical, maintainable construction. Equipment is built to be serviced, supported and upgraded over its working life." },
    { title: "Customer-Focused Engineering", desc: "Solutions are defined around the customer's process — temperature, cycle, throughput and site conditions — not around a catalogue." },
];

const About = () => (
    <div data-testid="about-page">
        <PageHero
            kicker="ABOUT US"
            title="Engineering Built"
            accent="Around Heat."
            sub="SS Engineering Works — Industrial Furnace Solutions, Since 2026."
            image={IMAGES.factory}
            crumb="About Us"
        />
        <section className="bg-white py-20 md:py-28">
            <div className="mx-auto grid max-w-[1440px] items-start gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:px-10">
                <div>
                    <Reveal><Kicker>THE COMPANY</Kicker></Reveal>
                    <h2 className="mt-4 font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-brand-charcoal">
                        <LineReveal>Industrial Furnace</LineReveal>
                        <LineReveal delay={0.1}>Manufacturing &</LineReveal>
                        <LineReveal delay={0.2} className="text-brand-red">Integrated Controls</LineReveal>
                    </h2>
                    <Reveal delay={0.2}>
                        <p className="mt-7 text-base leading-relaxed text-slate-600">
                            SS Engineering Works is an industrial engineering and manufacturing company specializing in industrial electric furnaces and associated heating, electrical, control, automation and process solutions.
                        </p>
                        <p className="mt-4 text-base leading-relaxed text-slate-600">
                            From our facility in Greater Noida, Uttar Pradesh, we engineer and manufacture furnace systems, control panels and automation solutions for demanding industrial heating applications.
                        </p>
                    </Reveal>
                </div>
                <div className="space-y-px bg-slate-200">
                    {PILLARS.map((p, i) => (
                        <Reveal key={p.title} delay={i * 0.08}>
                            <div className="group flex gap-5 bg-white p-7 transition-colors duration-300 hover:bg-brand-mist" data-testid={`about-pillar-${i}`}>
                                <CheckCircle2 size={22} strokeWidth={1.5} className="mt-1 shrink-0 text-brand-red" />
                                <div>
                                    <p className="font-display text-lg font-bold uppercase text-brand-charcoal">{p.title}</p>
                                    <p className="mt-2 text-sm leading-relaxed text-slate-500">{p.desc}</p>
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
        <section className="bg-brand-ink py-20 md:py-24 technical-grid grain relative">
            <div className="mx-auto grid max-w-[1440px] gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-10">
                <Reveal>
                    <div className="border-l-2 border-brand-red pl-8">
                        <p className="font-mono text-[11px] tracking-[0.24em] text-brand-ember uppercase">VISION</p>
                        <p className="mt-4 font-display text-xl sm:text-2xl font-semibold leading-relaxed text-white" data-testid="about-vision">
                            To develop dependable industrial heating and process solutions through engineering, technology and continuous improvement.
                        </p>
                    </div>
                </Reveal>
                <Reveal delay={0.12}>
                    <div className="border-l-2 border-brand-ember pl-8">
                        <p className="font-mono text-[11px] tracking-[0.24em] text-brand-ember uppercase">MISSION</p>
                        <p className="mt-4 font-display text-xl sm:text-2xl font-semibold leading-relaxed text-white" data-testid="about-mission">
                            To deliver practical, efficient and maintainable furnace and automation solutions aligned with customer process requirements.
                        </p>
                    </div>
                </Reveal>
            </div>
        </section>
        <section className="bg-white py-16">
            <div className="mx-auto flex max-w-[1440px] flex-wrap items-center justify-between gap-6 px-4 sm:px-6 lg:px-10">
                <p className="font-display text-2xl font-extrabold uppercase tracking-tight text-brand-charcoal">
                    {COMPANY.secondary}
                </p>
                <Link to="/contact" data-testid="about-contact-cta"
                    className="group flex items-center gap-3 bg-brand-red px-7 py-4 font-display text-sm font-bold tracking-[0.14em] text-white transition-colors hover:bg-brand-charcoal">
                    TALK TO OUR TEAM <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </Link>
            </div>
        </section>
    </div>
);

export default About;
